import React, { useState, useEffect } from 'react';
import { Search, MapPin, Building2, Copy, Check, Lock, Eye, EyeOff, Unlock } from 'lucide-react';
import { api } from '../utils/api';

interface Dealer {
  id: string;
  province: string;
  city: string;
  area_manager: string;
  manufacturer: string;
  dealer_name: string;
  dealer_code: string;
  mailing_address: string;
  financial_advisor: string;
  phone: string;
  phone2: string;
  phone3: string;
  email1: string;
  email2: string;
  license_number: string;
  notes: string;
}

interface Filters {
  provinces: string[];
  cities: string[];
  manufacturers: string[];
}

// 隐藏敏感信息
const maskInfo = (value: string): string => {
  if (!value) return '';
  if (value.length <= 2) return '***';
  return value.substring(0, 2) + '***' + value.substring(value.length - 2);
};

// 隐藏电话
const maskPhone = (value: string): string => {
  if (!value) return '';
  if (value.length <= 4) return '***';
  return value.substring(0, 3) + '****' + value.substring(value.length - 4);
};

// 隐藏姓名（隐藏中间一个字）
const maskName = (value: string): string => {
  if (!value) return '';
  if (value.length <= 1) return '*';
  if (value.length === 2) return value[0] + '*';
  // 3个字及以上：隐藏中间一个字
  const mid = Math.floor(value.length / 2);
  return value.substring(0, mid) + '*' + value.substring(mid + 1);
};

const DealersPage: React.FC = () => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [filters, setFilters] = useState<Filters>({ provinces: [], cities: [], manufacturers: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 访问验证
  const [isVerified, setIsVerified] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // 筛选条件
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [hasLicense, setHasLicense] = useState(''); // '' | 'yes' | 'no'
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // 加载数据
  const fetchDealers = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (province) params.set('province', province);
    if (city) params.set('city', city);
    if (manufacturer) params.set('manufacturer', manufacturer);
    if (hasLicense) params.set('hasLicense', hasLicense);
    if (keyword) params.set('keyword', keyword);
    params.set('page', String(page));
    params.set('pageSize', '20');

    try {
      const response: any = await api.get(`/api/dealers?${params.toString()}`);
      if (response.success !== false) {
        setDealers(response.data || []);
        setFilters(response.filters || { provinces: [], cities: [], manufacturers: [] });
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      console.error('加载失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealers();
  }, [province, city, manufacturer, hasLicense, page]);

  // 处理省份变化，重置城市选择
  const handleProvinceChange = (value: string) => {
    setProvince(value);
    setCity('');
    setPage(1);
  };

  // 验证密码
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError('');
    setVerifying(true);

    try {
      const response: any = await api.post('/api/dealers/verify', { password });
      if (response.success) {
        setIsVerified(true);
        setShowVerifyModal(false);
        setPassword('');
      } else {
        setVerifyError(response.error || '验证失败');
      }
    } catch (err) {
      setVerifyError('验证请求失败，请重试');
    } finally {
      setVerifying(false);
    }
  };

  // 复制信息（未验证时复制的是隐藏后的内容）
  const copyInfo = async (dealer: Dealer) => {
    const parts: string[] = [`【${dealer.dealer_name}】`];
    
    if (dealer.area_manager) {
      parts.push(`区域经理：${isVerified ? dealer.area_manager : maskName(dealer.area_manager)}`);
    }
    if (dealer.financial_advisor) {
      parts.push(`金融顾问：${isVerified ? dealer.financial_advisor : maskName(dealer.financial_advisor)}`);
    }
    if (dealer.phone) {
      const phoneDisplay = isVerified ? dealer.phone : maskPhone(dealer.phone);
      const phone2Display = dealer.phone2 ? (isVerified ? ' / ' + dealer.phone2 : ' / ' + maskPhone(dealer.phone2)) : '';
      parts.push(`电话：${phoneDisplay}${phone2Display}`);
    }
    if (dealer.mailing_address) {
      parts.push(`地址：${isVerified ? dealer.mailing_address : maskInfo(dealer.mailing_address)}`);
    }
    if (dealer.email1) {
      const email1Display = isVerified ? dealer.email1 : maskInfo(dealer.email1);
      const email2Display = dealer.email2 ? (isVerified ? ' / ' + dealer.email2 : ' / ' + maskInfo(dealer.email2)) : '';
      parts.push(`邮箱：${email1Display}${email2Display}`);
    }
    // 添加执照编号（如果有且已验证）
    if (dealer.license_number && isVerified) {
      parts.push(`执照编号：${dealer.license_number}`);
    }

    const text = parts.join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(dealer.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert('复制失败，请手动复制');
    }
  };

  // 验证弹窗
  const VerifyModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">查看完整信息</h2>
          <p className="text-gray-500 text-sm mt-2">请输入合同号查看完整信息</p>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                合同号
              </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入合同号"
                className="w-full border rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {verifyError && (
            <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
              {verifyError}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowVerifyModal(false)}
              className="flex-1 py-2 px-4 border rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={verifying || !password}
              className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {verifying ? '验证中...' : '验证'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">经销商查询</h1>
              <p className="text-blue-100">查找身边的广汽经销商</p>
            </div>
            {isVerified ? (
              <button
                onClick={() => setIsVerified(false)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg text-sm hover:bg-white/30 transition-colors"
              >
                <Unlock className="w-4 h-4" />
                退出验证
              </button>
            ) : (
              <button
                onClick={() => setShowVerifyModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg text-blue-600 text-sm hover:bg-blue-50 transition-colors"
              >
                <Lock className="w-4 h-4" />
                验证查看完整信息
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 搜索筛选 */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">省份</label>
              <select
                value={province}
                onChange={(e) => handleProvinceChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部省份</option>
                {filters.provinces.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">城市</label>
              <select
                value={city}
                onChange={(e) => { setCity(e.target.value); setPage(1); }}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部城市</option>
                {filters.cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">主机厂</label>
              <select
                value={manufacturer}
                onChange={(e) => { setManufacturer(e.target.value); setPage(1); }}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部主机厂</option>
                {filters.manufacturers.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">营业执照</label>
              <select
                value={hasLicense}
                onChange={(e) => { setHasLicense(e.target.value); setPage(1); }}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部</option>
                <option value="yes">有执照</option>
                <option value="no">无执照</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">关键词搜索</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
                  placeholder="搜索经销商名称..."
                  className="w-full border rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-gray-500">
              共找到 <span className="font-medium text-gray-800">{total}</span> 家经销商
            </p>
            {!isVerified && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <Lock className="w-4 h-4" />
                部分信息已隐藏，验证后可查看完整信息
              </p>
            )}
          </div>
        </div>

        {/* 经销商列表 */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">加载中...</div>
        ) : dealers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">没有找到匹配的经销商</div>
        ) : (
          <>
            <div className="space-y-4">
              {dealers.map((dealer) => (
                <div key={dealer.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpanded(expanded === dealer.id ? null : dealer.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-gray-800">{dealer.dealer_name}</h3>
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {dealer.province} {dealer.city}
                          </span>
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {dealer.manufacturer}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">金融顾问</p>
                        <p className="font-medium text-gray-800">
                          {isVerified ? (dealer.financial_advisor || '-') : maskName(dealer.financial_advisor || '-')}
                        </p>
                        <p className="text-blue-600 font-medium">
                          {isVerified ? dealer.phone : maskPhone(dealer.phone)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {expanded === dealer.id && (
                    <div className="border-t bg-gray-50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">经销商代码</p>
                          <p className="font-medium">{dealer.dealer_code || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">区域经理</p>
                          <p className="font-medium">{isVerified ? (dealer.area_manager || '-') : maskName(dealer.area_manager || '-')}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-500 mb-1">收件地址</p>
                          <p className="font-medium">
                            {isVerified ? dealer.mailing_address : maskInfo(dealer.mailing_address) || '-'}
                          </p>
                        </div>
                        {dealer.phone && (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">联系电话</p>
                            <p className="font-medium">
                              {isVerified ? dealer.phone : maskPhone(dealer.phone)}
                            </p>
                          </div>
                        )}
                        {dealer.phone2 && (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">备用电话</p>
                            <p className="font-medium">
                              {isVerified ? dealer.phone2 : maskPhone(dealer.phone2)}
                            </p>
                          </div>
                        )}
                        {dealer.email1 && (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">邮箱</p>
                            <p className="font-medium">
                              {isVerified ? dealer.email1 : maskInfo(dealer.email1)}
                            </p>
                          </div>
                        )}
                        {dealer.license_number && isVerified && (
                          <div>
                            <p className="text-sm text-gray-500 mb-1">执照编号</p>
                            <p className="font-medium">{dealer.license_number}</p>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyInfo(dealer); }}
                        className={`mt-4 w-full py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                          copiedId === dealer.id
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {copiedId === dealer.id ? (
                          <>
                            <Check className="w-4 h-4" />
                            已复制
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            {isVerified ? '复制信息' : '复制（隐藏版）'}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  首页
                </button>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  上一页
                </button>
                <span className="px-4 py-1">
                  第 {page} / {totalPages} 页
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  下一页
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  末页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 验证弹窗 */}
      {showVerifyModal && <VerifyModal />}
    </div>
  );
};

export default DealersPage;
